import { useEffect, useMemo, useRef, type CSSProperties } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Bot,
  Building2,
  Facebook,
  Globe,
  Layers,
  Mail,
  MessageCircle,
  Send,
  Store,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { i18n } from '@/lib/i18n'
import { BRAND_LOGOS, type BrandKey } from '@/lib/brand-logos'
import type { BetaKey } from './beta-channels'
import {
  PLATFORM,
  useModuleEnabled,
  useWidgets,
  useZaloAccounts,
  type ChannelAccount,
} from '@/hooks/use-integrations'
import { useMyPermissions } from '@/hooks/use-settings'

/**
 * Danh mục kênh của trang Tích hợp. Giống SaleSmartly: danh mục cố định ở
 * frontend, số tài khoản của từng kênh đếm từ dữ liệu thật.
 */
export type ChannelKey =
  | 'zalo-personal'
  | 'zalo-oa'
  | 'zalo-bot'
  | 'facebook'
  | 'tiktok-shop'
  | 'shopee'
  | 'website'
  | 'pancake'
  | 'telegram-bot'
  | 'telegram-app'
  | BetaKey

/** official = kết nối qua API chính thức; personal = tài khoản cá nhân chạy phiên trên máy chủ. */
export type ChannelKind = 'official' | 'personal'

export interface ChannelDef {
  key: ChannelKey
  name: string
  description: string
  /** Icon dự phòng khi kênh không có logo thương hiệu (Live Chat, Email). */
  icon: LucideIcon
  /** Chữ viết tắt thay icon (dự phòng, khi chưa có logo). */
  glyph?: string
  /** Logo thật của nền tảng — xem `lib/brand-logos.ts`. */
  logo?: BrandKey
  /** Kênh chưa có backend — trang kênh là giao diện chuẩn bị (xem beta-channels.ts). */
  beta?: boolean
  /** Chưa nằm trong đợt làm hiện tại → hiện mờ "Sắp ra mắt", không mở được. */
  comingSoon?: boolean
  /** Màu thương hiệu của kênh — dùng cho ô icon và dải màu nhạt trên thẻ. */
  brand: string
  kind: ChannelKind
  tag?: 'popular' | 'beta'
  /** Lọc tài khoản thuộc kênh từ danh sách `/zalo-accounts`. */
  platforms?: number[]
}

const PANCAKE_PLATFORMS = [30, 31, 32, 39]

/**
 * Kênh beta đang làm ở đợt này (xem docs/KET-NOI-UU-TIEN-TIKTOK-SHOPEE-TELEGRAM.md).
 * Còn lại hiện mờ "Sắp ra mắt". TikTok Shop chat làm trên kênh TikTok Shop sẵn có;
 * Shopee đã thành kênh thật (uỷ quyền shop), chat chờ Shopee whitelist.
 */
const PRIORITY_BETA = new Set<BetaKey>(['tiktok-app'])

// Khai báo TRƯỚC CHANNELS: mảng gọi beta() ngay khi nạp module.
export const CHANNELS: ChannelDef[] = [
  {
    key: 'zalo-personal',
    get name() { return i18n.t('common:channels.zaloPersonal') },
    get description() { return i18n.t('chanCatalog:catalog.zalo-personal.description') },
    icon: MessageCircle,
    logo: 'zalo',
    brand: '#0068FF',
    kind: 'personal',
    tag: 'popular',
    platforms: [PLATFORM.ZALO_USER],
  },
  {
    key: 'zalo-oa',
    name: 'Zalo OA',
    get description() { return i18n.t('chanCatalog:catalog.zalo-oa.description') },
    icon: Building2,
    logo: 'zalo',
    brand: '#0068FF',
    kind: 'official',
    tag: 'popular',
    platforms: [PLATFORM.ZALO_OA],
  },
  {
    key: 'zalo-bot',
    name: 'Zalo Bot',
    get description() { return i18n.t('chanCatalog:catalog.zalo-bot.description') },
    icon: Bot,
    logo: 'zalo',
    brand: '#0A8FDC',
    kind: 'official',
    tag: 'beta',
    platforms: [PLATFORM.ZALO_BOT],
  },
  {
    key: 'facebook',
    name: 'Facebook Messenger',
    get description() { return i18n.t('chanCatalog:catalog.facebook.description') },
    icon: Facebook,
    logo: 'messenger',
    brand: '#0866FF',
    kind: 'official',
    tag: 'popular',
    platforms: [PLATFORM.FACEBOOK_PAGE],
  },
  {
    key: 'telegram-bot',
    name: 'Telegram Bot',
    get description() { return i18n.t('chanCatalog:catalog.telegram-bot.description') },
    icon: Send,
    logo: 'telegram',
    brand: '#229ED9',
    kind: 'official',
    tag: 'beta',
    platforms: [PLATFORM.TELEGRAM],
  },
  {
    key: 'telegram-app',
    get name() { return i18n.t('common:channels.telegramPersonal') },
    get description() { return i18n.t('chanCatalog:catalog.telegram-app.description') },
    icon: Send,
    logo: 'telegram',
    brand: '#229ED9',
    // Telegram Business là API chính thức, không giữ phiên đăng nhập trên máy chủ.
    kind: 'official',
    tag: 'beta',
    platforms: [PLATFORM.TELEGRAM_BUSINESS],
  },
  {
    key: 'tiktok-shop',
    name: 'TikTok Shop',
    get description() { return i18n.t('chanCatalog:catalog.tiktok-shop.description') },
    icon: Store,
    logo: 'tiktok',
    brand: '#111111',
    kind: 'official',
    platforms: [PLATFORM.TIKTOK_SHOP],
  },
  {
    key: 'shopee',
    name: 'Shopee',
    get description() { return i18n.t('chanCatalog:catalog.shopee.description') },
    icon: Store,
    logo: 'shopee',
    brand: '#EE4D2D',
    kind: 'official',
    platforms: [PLATFORM.SHOPEE],
  },
  {
    key: 'website',
    name: 'Live Chat website',
    get description() { return i18n.t('chanCatalog:catalog.website.description') },
    icon: Globe,
    brand: '#0045FF',
    kind: 'official',
  },
  {
    key: 'pancake',
    name: 'Pancake',
    get description() { return i18n.t('chanCatalog:catalog.pancake.description') },
    icon: Layers,
    logo: 'pancake',
    brand: '#1260E4',
    kind: 'official',
    platforms: PANCAKE_PLATFORMS,
  },

  // ── BETA: giao diện chuẩn bị, chưa kết nối thật (docs/KET-NOI-KENH-MOI-API.md) ──
  beta('whatsapp-api', 'WhatsApp API', () => i18n.t('chanCatalog:catalog.whatsapp-api.description'), '#25D366', 'official', { logo: 'whatsapp' }),
  beta('whatsapp-app', () => i18n.t('chanCatalog:catalog.whatsapp-app.name'), () => i18n.t('chanCatalog:catalog.whatsapp-app.description'), '#25D366', 'personal', { logo: 'whatsapp' }),
  beta('instagram', 'Instagram', () => i18n.t('chanCatalog:catalog.instagram.description'), '#E1306C', 'official', { logo: 'instagram' }),
  beta('facebook-comments', () => i18n.t('chanCatalog:catalog.facebook-comments.name'), () => i18n.t('chanCatalog:catalog.facebook-comments.description'), '#0866FF', 'official', { logo: 'facebook' }),
  beta('email', 'Email', () => i18n.t('chanCatalog:catalog.email.description'), '#EA4335', 'official', { icon: Mail }),
  beta('line', 'LINE OA', () => i18n.t('chanCatalog:catalog.line.description'), '#06C755', 'official', { logo: 'line' }),
  beta('line-app', () => i18n.t('chanCatalog:catalog.line-app.name'), () => i18n.t('chanCatalog:catalog.line-app.description'), '#06C755', 'personal', { logo: 'line' }),
  beta('tiktok-business', 'TikTok Business', () => i18n.t('chanCatalog:catalog.tiktok-business.description'), '#111111', 'official', { logo: 'tiktok' }),
  beta('tiktok-app', () => i18n.t('chanCatalog:catalog.tiktok-app.name'), () => i18n.t('chanCatalog:catalog.tiktok-app.description'), '#111111', 'personal', { logo: 'tiktok' }),
  beta('youtube', () => i18n.t('chanCatalog:catalog.youtube.name'), () => i18n.t('chanCatalog:catalog.youtube.description'), '#FF0000', 'official', { logo: 'youtube' }),
  beta('x', 'X (Twitter)', () => i18n.t('chanCatalog:catalog.x.description'), '#111111', 'official', { logo: 'x' }),
  beta('wechat', 'WeChat OA', () => i18n.t('chanCatalog:catalog.wechat.description'), '#07C160', 'official', { logo: 'wechat' }),
  beta('wecom', 'WeCom', () => i18n.t('chanCatalog:catalog.wecom.description'), '#2B7FF6', 'official', { logo: 'wecom' }),
  beta('vk', 'VKontakte', () => i18n.t('chanCatalog:catalog.vk.description'), '#0077FF', 'official', { logo: 'vk' }),
  beta('slack', 'Slack', () => i18n.t('chanCatalog:catalog.slack.description'), '#4A154B', 'official', { logo: 'slack' }),
]

function beta(
  key: BetaKey,
  name: string | (() => string),
  description: () => string,
  brand: string,
  kind: ChannelKind,
  extra: { icon?: LucideIcon; glyph?: string; logo?: BrandKey },
): ChannelDef {
  return {
    key,
    get name() { return typeof name === 'function' ? name() : name },
    get description() { return description() },
    brand,
    kind,
    beta: true,
    icon: extra.icon ?? MessageCircle,
    glyph: extra.glyph,
    logo: extra.logo,
    comingSoon: !PRIORITY_BETA.has(key),
    // Nhãn "Beta" thắng "Phổ biến": người dùng cần biết trước là chưa chạy thật.
    tag: 'beta',
  }
}

export const CHANNEL_BY_KEY = Object.fromEntries(CHANNELS.map((c) => [c.key, c])) as Record<
  ChannelKey,
  ChannelDef
>

export function isChannelKey(v: string | null | undefined): v is ChannelKey {
  return !!v && v in CHANNEL_BY_KEY
}

/**
 * Ô icon vuông bo góc. Có logo thương hiệu → nền trắng + viền, logo ~60% ô
 * (nền trắng cả ở dark mode để logo đen như X/TikTok vẫn đọc được). Không có
 * logo (Live Chat, Email) → icon trắng trên nền màu thương hiệu như cũ.
 */
export function ChannelIcon({ def, size = 'md' }: { def: ChannelDef; size?: 'sm' | 'md' | 'lg' }) {
  const box = cn(
    'flex shrink-0 items-center justify-center',
    size === 'sm' && 'h-5 w-5 rounded-[5px]',
    size === 'md' && 'h-8 w-8 rounded-lg',
    size === 'lg' && 'h-10 w-10 rounded-lg',
  )
  if (def.logo) {
    return (
      <span className={cn(box, 'border border-black/10 bg-white')}>
        <img src={BRAND_LOGOS[def.logo].src} alt="" draggable={false} className="h-[62%] w-[62%] object-contain" />
      </span>
    )
  }
  const Icon = def.icon
  return (
    <span
      className={cn(
        box,
        'font-bold leading-none tracking-tight text-white',
        def.glyph && (size === 'sm' ? 'text-[7px]' : size === 'md' ? 'text-[10px]' : 'text-xs'),
        size === 'sm' && '[&_svg]:h-3 [&_svg]:w-3',
        size === 'md' && '[&_svg]:h-4 [&_svg]:w-4',
        size === 'lg' && '[&_svg]:h-5 [&_svg]:w-5',
      )}
      style={{ backgroundColor: def.brand }}
    >
      {def.glyph ?? <Icon />}
    </span>
  )
}

/**
 * Dải màu thương hiệu nhạt dần sang nền thẻ (thẻ kênh ở trang Tích hợp, màn trống Hộp thư, trang Bắt đầu).
 * Màu kênh là dữ liệu, không phải token theme; nền đích là `--card` nên dark mode tự đúng.
 */
export function channelBandStyle(def: Pick<ChannelDef, 'brand'>): CSSProperties {
  return { backgroundImage: `linear-gradient(90deg, ${def.brand}17 0%, hsl(var(--card)) 45%)` }
}

/** Khung thẻ kênh dùng chung (viền, bo góc, nổi bóng khi rê chuột) — đi cùng `channelBandStyle`. */
export const CHANNEL_TILE_CLASS = 'relative flex flex-col rounded-lg border bg-card p-4 transition-shadow hover:shadow-md'

export interface ChannelStats {
  total: number
  active: number
}

/**
 * Đếm tài khoản theo kênh. Dùng chung cache `useZaloAccounts()` (mọi platform)
 * mà các tab con cũng gọi, nên không phát sinh thêm lượt gọi API.
 */
export function useChannelStats() {
  const accounts = useZaloAccounts()
  const widgets = useWidgets()
  const pancakeOn = useModuleEnabled('pancake')

  const stats = useMemo(() => {
    const out = {} as Record<ChannelKey, ChannelStats>
    const list: ChannelAccount[] = accounts.data ?? []
    for (const c of CHANNELS) {
      if (c.key === 'website') {
        const ws = widgets.data ?? []
        out[c.key] = { total: ws.length, active: ws.filter((w) => w.isActive).length }
        continue
      }
      const mine = list.filter((a) => c.platforms?.includes(a.platform))
      out[c.key] = {
        total: mine.length,
        active: mine.filter((a) => a.liveStatus === 'connected' && !a.isDisabled).length,
      }
    }
    return out
  }, [accounts.data, widgets.data])

  const channels = useMemo(
    () =>
      CHANNELS.filter((c) => c.key !== 'pancake' || pancakeOn).sort(
        (a, b) => Number(!!a.comingSoon) - Number(!!b.comingSoon),
      ),
    [pancakeOn],
  )

  return { stats, channels, isLoading: accounts.isLoading || widgets.isLoading }
}

/**
 * Nút "Thêm" ở lưới kênh mở trang kênh kèm `?add=1` → chạy luôn luồng kết nối
 * (một lần), rồi dọn tham số để refresh không chạy lại. `ready` = false khi
 * luồng chưa chạy được (vd máy chủ chưa cấu hình khoá ứng dụng).
 * Thiếu `integrations.create` (Quản lý / Nhân viên mặc định) → chỉ dọn tham số, không mở luồng kết nối.
 */
export function useAutoAdd(run: () => void, ready = true) {
  const [sp, setSp] = useSearchParams()
  const fired = useRef(false)
  const wantAdd = sp.get('add') === '1'
  // Chờ tải xong quyền rồi mới quyết định — đang tải mà coi là "không có" thì mất luôn ?add=1 của quản trị viên.
  const { data: perms } = useMyPermissions()
  const canAdd = !!perms?.has('integrations.create')
  useEffect(() => {
    if (!wantAdd || fired.current || !perms || (canAdd && !ready)) return
    fired.current = true
    setSp(
      (p) => {
        p.delete('add')
        return p
      },
      { replace: true },
    )
    if (canAdd) run()
    // run đổi mỗi lần render — chỉ cần chạy đúng một lần khi đủ điều kiện.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantAdd, ready, perms, canAdd])
  return wantAdd
}

/** Mô tả ô trống của trang kênh khi người xem thiếu `integrations.create` (nút Kết nối đã bị ẩn). */
export function noConnectHint(): string {
  return i18n.t('chanCatalog:catalog.noConnectHint')
}

/** @deprecated Giá trị tiếng Việt cố định — dùng `noConnectHint()` để theo ngôn ngữ đang chọn. */
export const NO_CONNECT_HINT = 'Chỉ chủ sở hữu / quản trị viên kết nối được kênh.' // i18n-ignore
